import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import StoreLayout from 'layouts/StoreLayout';
import AuthLayout from 'layouts/AuthLayout';
import AccountLayout from 'layouts/AccountLayout';
import CheckoutLayout from 'layouts/CheckoutLayout';
import AdminLayout from 'layouts/AdminLayout';
import PrivateRoute from 'guards/PrivateRoute';
import GuestRoute from 'guards/GuestRoute';
import AdminRoute from 'guards/AdminRoute';
import AdminGuestRoute from 'guards/AdminGuestRoute';
import LoadingSkeleton from 'components/feedback/LoadingSkeleton';
import { ROUTES } from 'config/routes';
import { publicRoutes } from './publicRoutes';
import { guestRoutes } from './accountRoutes';
import { checkoutRoutes } from './checkoutRoutes';
import { adminRoutes } from './adminRoutes';
import {
  AccountPage,
  ProfilePage,
  AddressesPage,
  OrdersPage,
  OrdersInPreparationPage,
  CurrentAccountPage,
  PaymentNoticesPage,
  FavoritesPage,
  HistoryPage,
  QuickOrderPage,
  StoreUnavailablePage,
} from 'pages';

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSkeleton rows={3} />}>{children}</Suspense>
);

const AppRouter: React.FC = () => (
  <Routes>
    <Route path={ROUTES.STORE_UNAVAILABLE} element={<Lazy><StoreUnavailablePage /></Lazy>} />

    <Route element={<StoreLayout />}>
      {publicRoutes}
      <Route element={<PrivateRoute />}>
        <Route element={<AccountLayout />}>
          <Route path={ROUTES.ACCOUNT} element={<Lazy><AccountPage /></Lazy>} />
          <Route path={ROUTES.PROFILE} element={<Lazy><ProfilePage /></Lazy>} />
          <Route path={ROUTES.ADDRESSES} element={<Lazy><AddressesPage /></Lazy>} />
          <Route path={ROUTES.ORDERS} element={<Lazy><OrdersPage /></Lazy>} />
          <Route path={ROUTES.ORDERS_IN_PREPARATION} element={<Lazy><OrdersInPreparationPage /></Lazy>} />
          <Route path={ROUTES.CURRENT_ACCOUNT} element={<Lazy><CurrentAccountPage /></Lazy>} />
          <Route path={ROUTES.PAYMENT_NOTICES} element={<Lazy><PaymentNoticesPage /></Lazy>} />
          <Route path={ROUTES.FAVORITES} element={<Lazy><FavoritesPage /></Lazy>} />
          <Route path={ROUTES.HISTORY} element={<Lazy><HistoryPage /></Lazy>} />
          <Route path={ROUTES.QUICK_ORDER} element={<Lazy><QuickOrderPage /></Lazy>} />
        </Route>
      </Route>
    </Route>

    <Route element={<GuestRoute />}>
      <Route element={<AuthLayout />}>{guestRoutes}</Route>
    </Route>

    <Route element={<PrivateRoute />}>
      <Route element={<CheckoutLayout />}>{checkoutRoutes}</Route>
    </Route>

    <Route path={ROUTES.ADMIN_AUTH_CALLBACK} element={<Lazy>{adminRoutes.callback}</Lazy>} />

    <Route element={<AdminGuestRoute />}>
      <Route path={ROUTES.ADMIN_LOGIN} element={<Lazy>{adminRoutes.login}</Lazy>} />
    </Route>

    <Route element={<AdminRoute />}>
      <Route path={ROUTES.ADMIN} element={<AdminLayout />}>
        {adminRoutes.protected}
      </Route>
    </Route>

    <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
  </Routes>
);

export default AppRouter;
